// FamilyStatusCard — Jul 2026 (Randy, Phase C, Scenario G).
// Self-contained card for Account Settings: renders ONLY when the signed-in
// account is family-restricted. Shows every household link, who the guardian
// is, and the two self-service exits (request release / leave household).
import { useEffect, useState } from "react";
import { ShieldWarning, SignOut, HandWaving } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";

export default function FamilyStatusCard() {
  const [status, setStatus] = useState(null);
  const me = getUser();

  const load = async () => {
    try {
      const r = await api.get("/me/family-status");
      setStatus(r.data);
    } catch { /* older server or signed out */ }
  };
  useEffect(() => { load(); }, []);

  if (!status?.restricted) return null;

  const requestRelease = async (l) => {
    try {
      await api.post(`/me/family-links/${l.link_id}/request-release`);
      toast.success("Release requested — your parent will see it on their household panel.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed."); }
  };

  const leave = async (l) => {
    if (!window.confirm(`Leave “${l.silo_name}”? You'll lose the family calendar, and the restriction from this household ends immediately. Any subscriptions a parent paid for will finish the paid month, then stop.`)) return;
    try {
      await api.delete(`/silos/${l.silo_id}/collaborators/${me.id}`);
      toast.success("You've left the household.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not leave."); }
  };

  return (
    <section className="bg-[#F8F1E2] border border-[#E5C77F] rounded-lg p-6" data-testid="family-status-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldWarning size={18} weight="duotone" className="text-[#8A5A00]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Family-restricted account</h3>
      </div>
      <p className="text-xs text-[#6B6A68] leading-relaxed max-w-2xl">
        This account has <strong>purchase guardrails</strong>: every checkout requires typing a card fresh —
        no saved cards. Everything else works normally. The restriction ends when a parent releases it,
        or automatically when you leave the household.
      </p>
      <div className="mt-4 space-y-2">
        {(status.links || []).map((l) => (
          <div key={l.link_id} className="flex flex-wrap items-center gap-3 bg-white border hairline rounded px-3 py-2 text-sm" data-testid={`family-link-row-${l.link_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[#1C1B1A] truncate"><strong>{l.silo_name || "Household"}</strong></div>
              <div className="text-[11px] text-[#6B6A68] truncate">linked to {l.guardian_name || "your parent"}'s household</div>
            </div>
            {l.release_requested_at ? (
              <span className="text-[11px] text-[#8A5A00] italic" data-testid={`release-pending-${l.link_id}`}>
                Release requested — waiting on your parent
              </span>
            ) : (
              <button onClick={() => requestRelease(l)} data-testid={`request-release-${l.link_id}`}
                className="text-xs px-2.5 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1">
                <HandWaving size={13} /> Request release
              </button>
            )}
            <button onClick={() => leave(l)} data-testid={`leave-household-${l.link_id}`}
              className="text-xs px-2.5 py-1.5 rounded-md border hairline text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1">
              <SignOut size={13} /> Leave household
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
